import { toast } from '@/lib/toast';
import { useBulkSetInventory, useLocations } from '@/hooks/useVariants';
import { InlineEditCell } from './InlineEditCell';

interface VariantInventoryCellProps {
  shopifyVariantId: number;
  shopifyInventoryItemId: number;
  /** Current on-hand quantity. */
  value: number | null;
  className?: string;
}

/**
 * Click-to-edit stock quantity for a variant. Edits the *primary* location
 * (the first active location) and pushes straight to Shopify — for per-location
 * control on multi-location items, use the inventory table on the product
 * detail page. Self-contained, like the other inline cells.
 */
export function VariantInventoryCell({
  shopifyVariantId,
  shopifyInventoryItemId,
  value,
  className,
}: VariantInventoryCellProps) {
  const { data: locations = [] } = useLocations();
  const setInventory = useBulkSetInventory();
  const primary = locations[0];

  const commit = (next: string | number | null) => {
    if (next == null) {
      toast.error('Enter a stock quantity');
      return;
    }
    if (!primary) {
      toast.error('No store location is available to set stock');
      return;
    }
    setInventory.mutate(
      {
        items: [
          {
            shopify_inventory_item_id: shopifyInventoryItemId,
            shopify_variant_id: shopifyVariantId,
          },
        ],
        locationId: primary.shopify_location_id,
        available: Number(next),
      },
      {
        onSuccess: () => toast.success('Stock saved to Shopify'),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
      },
    );
  };

  return (
    <InlineEditCell
      value={value}
      kind="number"
      onCommit={commit}
      pending={setInventory.isPending}
      emptyLabel="0"
      className={className}
    />
  );
}
